import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { AuctionDownloadedService } from '@core/services/auction-downloaded.service';
import { faHeart, faTags } from '@fortawesome/free-solid-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CategoriesView } from '@shared/interfaces/api.interface';
import {
  FilterStatus,
  IAuctionFilterDto,
} from '@shared/interfaces/search.interface';
import { FilterService } from '@shared/services/filter.service';
import { LanguageService } from '@shared/services/language.service';
import { SearchService } from '@shared/services/search.service';
import { TagInfo } from '../../../tags-manager/interfaces/tag.interface';

import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalUtilsService } from '@core/services/modal-utils.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BidSearchResultComponent } from '@shared/components/bid-search-result/bid-search-result.component';
import { TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ArticleService } from 'src/app/articles-manager/services/article.service';
import { AuctionView } from '../../../articles-manager/interfaces/auction.interface';
import { CategoryTreeDialog } from '../../components/category-modal/category-modal.component';
import { SlideshowComponent } from '../../components/slideshow/slideshow.component';

@Component({
  selector: 'app-tags-special',
  templateUrl: './special.component.html',
  styleUrls: ['./special.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CategoryTreeDialog,
    TranslatePipe,
    NgIf,
    BidSearchResultComponent,
    FormsModule,
    ButtonModule,
    SlideshowComponent,
    FaIconComponent,
  ],
})
@UntilDestroy()
export class SpecialComponent {
  faTags = faTags;
  faHeart = faHeart;
  faSearch = faSearch;

  searchText = '';

  categories = signal<Array<CategoriesView>>([]);
  treeCategories = signal<TreeNode<CategoriesView>[]>([]);
  selectedCategories = signal<TreeNode[]>([]);
  auctionsCount = signal<number>(0);

  isLoaded = signal<boolean>(false);
  idAuctionList = signal<Array<number>>([]);
  memorabidAuctions = signal<Array<AuctionView>>([]);
  marketplaceAuctions = signal<Array<AuctionView>>([]);
  idAuctionType = signal<FilterStatus>(-1);

  tag = input<TagInfo>();
  metaEventSlice = input<number>(10);

  @Output('updateMeta') updateMetaEvent = new EventEmitter<{
    memorabidAuctions: AuctionView[];
  }>();

  private readonly articleService = inject(ArticleService);

  public auctionDownloadedService: AuctionDownloadedService = inject(
    AuctionDownloadedService
  );
  readonly searchService: SearchService = inject(SearchService);
  readonly languageService: LanguageService = inject(LanguageService);
  readonly translate: TranslateService = inject(TranslateService);
  private readonly modalUtils: ModalUtilsService = inject(ModalUtilsService);
  readonly filterService: FilterService<IAuctionFilterDto> = inject(
    FilterService<IAuctionFilterDto>
  );

  tagTranslated = computed(
    () =>
      this.tag().tagTranslatedList.find(
        (tag) =>
          tag.idLanguage ===
          this.languageService.userSelectedLanguage.idLanguage
      ) ?? this.tag().tagTranslatedList?.[0]
  );
  tagNameToShow = computed(() => this.tagTranslated().translatedValue);
  logoFileNameView = computed(() => this.tag().logoFileNameView);
  title = computed(() => this.tagTranslated().title);
  description = computed(() => this.tagTranslated().description);

  constructor() {
    effect(
      (fn) => {
        const sub = this.languageService.selectedLanguage$
          .pipe(untilDestroyed(this))
          .subscribe((lang) => {
            if (!lang) {
              return;
            }

            this.filterService.setDefaultFilters({
              orderBy: 'evidence',
              orderDirection: 'desc',
              ...this.filterService.filters,
              idLanguage: lang.idLanguage,
              idAuctionType: this.searchService.auctionTypeList[0]?.key,
              idDetail: null,
              onlySigned: false,
              idSport: null,
              idCompetition: null,
              idTeam: null,
              onlyNew: false,
              onlyExpiring: false,
              onlyArriving: false,
              auctionStatus: FilterStatus.OnlyActive,
              idEntity: null,
              idCampaign: null,
              idPromoter: null,
              idActiveAuction: null,
              idTagList: [this.tag().idTag],
            });

            this.searchText = this.filterService.filters.searchText || '';
            this.auctionsCount.set(this.auctionDownloadedService.auctionCount);

            this.auctionDownloadedService.memorabidAuctionSliceEnd = 30000;
            this.loadData(true);
            this.loadCategory();
          });

        fn(() => {
          this.auctionDownloadedService.marketplaceAuctionSliceEnd = 30;
          this.auctionDownloadedService.memorabidAuctionSliceEnd = 30;
          sub.unsubscribe();
        });
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  ngOnDestroy() {}

  loadCategory() {
    this.searchService
      .getRelatedTagCategories(this.tag().idTag)
      .pipe(untilDestroyed(this))
      .subscribe((rData) => {
        this.categories.set(
          rData?.sort((a, b) => b.auctionsCount - a.auctionsCount) ?? []
        );
        this.treeCategories.set(this.generateCategoriesTree(this.categories()));
      });
  }

  loadData(backToPosition: boolean = false) {
    this.searchService
      .getAllV2({
        ...this.filterService.getFilters(),
      })
      .pipe(untilDestroyed(this))
      .subscribe((data: AuctionView[]) => {
        const filteredData = data.filter(
          (a: AuctionView) =>
            this.articleService.favorites.value.has(a.idArticle) ||
            !this.filterService.filters.onlyFavorites
        );
        this.idAuctionList.set([
          ...filteredData
            .filter((a: AuctionView) => !a.isFinished)
            .filter((a: AuctionView) => !a.buyItNow)
            .map((a: AuctionView) => a.idAuction),
        ]);
        this.auctionDownloadedService.memorabidAuctions = filteredData;
        this.memorabidAuctions.set(filteredData);

        this.isLoaded.set(true);

        if (
          (this.filterService.filters.idCategoriesList?.length ?? 0) === 0 &&
          (this.auctionsCount() ?? 0) === 0
        ) {
          this.auctionsCount.set(
            this.auctionDownloadedService.memorabidAuctions?.length || 0
          );
          this.auctionDownloadedService.auctionCount = this.auctionsCount();
        }

        this.updateMetaEvent.emit({
          memorabidAuctions: this.memorabidAuctions().slice(
            this.metaEventSlice()
          ),
        });

        if (backToPosition) {
          setTimeout(() => {
            this.auctionDownloadedService.restoreScrollPosition(
              this.searchService.searchRoute,
              'auto'
            );
          }, 200);
        }
      });
  }

  private generateCategoriesTree(categories: CategoriesView[]) {
    const idMap = new Map(categories.map((x) => [x.id, x]));
    const headNodes = categories.filter((e) => !idMap.has(e.idParentCategory));
    const langCode = this.languageService.userSelectedLanguage.code;

    const arrayToHierarchy = (parent: CategoriesView) => {
      const children = [...idMap.values()]
        .filter((e) => e.idParentCategory === parent.id)
        .sort((a, b) => b.auctionsCount - a.auctionsCount);

      const label = parent.name[langCode];

      return {
        key: parent.id,
        label,
        data: parent,
        children: children.map((e) => arrayToHierarchy(e)),
      };
    };

    return headNodes.map((node) => arrayToHierarchy(node));
  }

  onOpenCategoryModal() {
    const ref = this.modalUtils.openModal(CategoryTreeDialog);
    ref.componentInstance.label.set(
      this.translate.instant('IDS_ARTICLE_CATEGORY')
    );
    ref.componentInstance.selectedCategories.set(this.selectedCategories());
    ref.componentInstance.auctionsCount.set(this.auctionsCount());
    ref.componentInstance.treeCategories.set(this.treeCategories());
    ref.componentInstance['onCallback'].subscribe((event: TreeNode[]) => {
      this.selectedCategories.set(event);
      this.filterService.setFilterField(
        'idCategoriesList',
        event.map((n) => parseInt(n.key?.toString() || '0'))
      );
      this.loadData();
      ref.close();
      setTimeout(() => {
        window.scrollTo({ top: 2, left: 0, behavior: 'auto' });
      }, 200);
    });
  }

  onFilterFavorites() {
    this.filterService.setFilterField(
      'onlyFavorites',
      !this.filterService.filters.onlyFavorites
    );
    this.loadData();
  }

  onSearch() {
    this.filterService.setBulkFilterFields({
      searchText: this.searchText,
    });

    this.loadData();
  }
}
